import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import Logo from './Logo';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CustomerIcon, ExpensesIcon, HomeIcon, InvoiceIcon, SettingIcon, LogoutIcon, InventoryIcon, ReportIcon } from '../assets/icons';
import userPool from '../utils/userPool';
import { STORAGE_REFRESH_TOKEN, STORAGE_TOKEN, USERNAME } from '../constants';
import toggleMenu from '../../src/assets/icons/svgicons/toggleMenu.svg';
import { CloseIcon } from '../assets/icons';


interface MobileProps {
    handleShowMobileMenu: () => void
}

const MobileSideMenu = ({handleShowMobileMenu}: MobileProps) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
 
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const closeMenuOnOutsideClick = (e: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
          setIsMenuOpen(false);
        }
      };
    
      const handleGlobalClick = (e: MouseEvent) => {
        closeMenuOnOutsideClick(e);
      };
    
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setIsMenuOpen(false);
        }
      };
    
      const handleClick = (e: MouseEvent) => {
        handleGlobalClick(e);
      };
    
      const handleKey = (e: KeyboardEvent) => {
        handleKeyDown(e);
      };
    
      if (isMenuOpen) {
        document.addEventListener('click', handleClick);
        document.addEventListener('keydown', handleKey);
      }
    
      return () => {
        document.removeEventListener('click', handleClick);
        document.removeEventListener('keydown', handleKey);
      };
    }, [isMenuOpen]);
    
    
    const menuItems = [
      {
        paths: ['/dashboard'],
        label: 'Home',
        icon: <HomeIcon />,
        to: '/dashboard',
      },
      {
        paths: ['/invoices', '/invoices/create', '/invoices/:invoiceNo/view'],
        label: 'Invoices',
        icon: <InvoiceIcon />,
        to: '/invoices',
      },
      {
        paths: ['/expenses', '/expenses/create', '/expenses/expenseName/view'],
        label: 'Expenses',
        icon: <ExpensesIcon />,
        to: '/expenses',
      },
      {
        paths: ['/customers', '/customers/create', '/customers/customerName/view'],
        label: 'Customers',
        icon: <CustomerIcon />,
        to: '/customers',
      },
      {
        paths: ['/settings'],
        label: 'Settings',
        icon: <SettingIcon />,
        to: '/settings',
      },
      {
        paths: ['/inventories'],
        label: 'Inventory',
        icon: <InventoryIcon />,
        to: '/inventories',
      },
      {
        paths: ['/reports'],
        label: 'Reports',
        icon: <ReportIcon />,
        to: '/reports',
      }
    ];

    // const menuRef = useRef(null);

   
  
    const toggleSidebar = () => {
      setIsCollapsed(!isCollapsed);
    };
  
    const handleLogout = () => {
      const user = userPool.getCurrentUser();
      if (user) {
        user.signOut();
        sessionStorage.removeItem(STORAGE_TOKEN);
        sessionStorage.removeItem(STORAGE_REFRESH_TOKEN);
        sessionStorage.removeItem(USERNAME);
        navigate('/auth');
      } else {
        console.error('No user found in the current session.');
      }
    };
  
    const isActive = (paths: string[]) => {
      return paths.some((path) => pathname.startsWith(path));
    };
  
    const renderMenuItem = (menuItem: any) => {
      const handleMenuItemClick = () => {
        setIsMenuOpen(false);
        handleShowMobileMenu();
      };
      return (
        <li key={menuItem.to} className="group mb-3 mt-4">
          <Link
            to={menuItem.to}
            onClick={handleMenuItemClick}
            className={cx({
              'h-[55px] flex items-center': true,
              'w-[212px]': !isCollapsed,
              'w-[59px]': isCollapsed,
              "before:content-[''] before:w-[10px] before:bg-[#FF6702]": isActive(menuItem.paths),
              'before:rounded-l-[10px] before:h-[55px] bg-[#C9EBF3]': isActive(menuItem.paths),
              'before:relative before:left-[-5px] rounded-r-[5px]': isActive(menuItem.paths),
              "hover:before:content-[''] hover:before:w-[10px] ": true,
              'hover:before:rounded-l-[10px] hover:before:h-[55px] mr-[10px] ml-[20px] hover:bg-[#C9EBF3]': true,
              'hover:before:relative hover:before:left-[-5px] hover:rounded-r-[5px] mr-[10px]': true,
              'text-[#222222]': isActive(menuItem.paths),
              'text-white': !isActive(menuItem.paths),
              'hover:text-[#222222]': true,
              collapsed: isCollapsed,
            })}
          >
            <div className="flex items-center justify-between w-[59px] h-full px-4">
              <div className="flex items-center space-x-4">
                <span className="text-lg font-light">{menuItem.icon}</span>
                {!isCollapsed && <span className="font-medium text-base">{menuItem.label}</span>}
              </div>
            </div>
          </Link>
        </li>
      );
    };


    return (
      <aside
      ref={menuRef}
        className={cx('bg-[#0A78B2] block lg:hidden relative min-h-screen justify-center items-center ',  {
          'w-[92px]': isCollapsed,
          'w-[250px]': !isCollapsed,
        })}
      >
         {isMenuOpen && ( 
        <div
          className="fixed inset-0 bg-black opacity-50 z-10"
          onClick={() => setIsMenuOpen(false)} 
        ></div>
      )}
       <div className="w-full flex flex-col items-center justify-center">
        <div className="flex w-[250px] items-center gap-[50px] justify-center p-4">
          <span className='w-[100px]'><Logo /></span> 
          <span className='text-white' onClick={handleShowMobileMenu}><CloseIcon/></span>
        </div>
      
        <nav className=''>
          <ul className='flex flex-col items-center justify-center'>
            {menuItems.map((menuItem) => renderMenuItem(menuItem))}
          </ul>
        </nav>
        </div> 
        


        <button
      onClick={handleLogout}
      className="flex items-center mt-[140px] w-[220px] text-white rounded hover:bg-opacity-90 transition duration-200 hover:before:bg-[#FF6702] hover:before:content-[''] hover:before:w-[10px] hover:before:rounded-l-[14px] hover:before:h-[55px] hover:bg-[#C9EBF3] hover:text-[#222222] hover:before:relative hover:before:left-[px] hover:rounded-r-[px]"
      style={{ marginRight: "10px", marginLeft: "10px" }}
    >
      <span className="ml-12 gap-[10px] flex font-medium">
        <LogoutIcon />
        Logout
      </span>
    </button>
        
    
  
      </aside>
    );
  };
  
  export default MobileSideMenu;
  
